import { Link } from 'react-router-dom';
import { SEO } from '../../../utils/seo';

export default function PortfolioMonitoringPage() {
  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Portfolio Monitoring & Asset Oversight | Ongoing Risk Visibility"
        description="Ongoing drone monitoring for land, infrastructure, and asset portfolios. Reduce surprises, document change, and protect long-term value."
        keywords="Portfolio monitoring services, Asset monitoring drone inspections, Land and infrastructure portfolio oversight, Ongoing drone inspection services"
        canonical="/services/portfolio-monitoring"
      />

      {/* Hero Section */}
      <section className="relative h-[85vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/f2fe24b1726da39dc6d5f22fc1b49daa.jpg"
            alt="Portfolio monitoring aerial view"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>
        
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center text-white">
          <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
            You Can't Manage What You Don't Monitor
          </h1>
          <p className="text-xl md:text-2xl mb-10 leading-relaxed text-white/95 max-w-3xl mx-auto">
            One-time inspections catch snapshots. Portfolio monitoring catches change before it becomes loss, liability, or downtime.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => scrollToSection('how-it-works')}
              className="px-8 py-4 bg-white text-gray-900 font-semibold rounded-lg hover:bg-gray-100 transition-colors whitespace-nowrap"
            >
              See How It Works
            </button>
            <button
              onClick={() => scrollToSection('cta-section')}
              className="px-8 py-4 bg-transparent text-white font-semibold rounded-lg border-2 border-white hover:bg-white/10 transition-colors whitespace-nowrap"
            >
              Request a Consultation
            </button>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-gray-900 mb-8 text-center">
            Why One-Off Inspections Fall Short
          </h2>
          <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed">
            <p className="text-xl mb-6">
              Most asset failures aren't sudden.<br />
              They're gradual and undocumented.
            </p>
            <div className="space-y-3 text-lg">
              <p>Erosion compounds.</p>
              <p>Structures degrade quietly.</p>
              <p>Timber health shifts.</p>
              <p>Compliance gaps grow unnoticed.</p>
            </div>
            <p className="text-xl mt-8 font-medium text-gray-900">
              Portfolio monitoring exists to track change over time, not explain problems after they happen.
            </p>
          </div>
        </div>
      </section>

      {/* What We Monitor */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-gray-900 mb-16 text-center">
            What We Monitor
          </h2>
          
          <div className="grid md:grid-cols-2 gap-12">
            {/* Area 1 */}
            <div className="bg-gray-50 rounded-lg overflow-hidden">
              <div className="h-64">
                <img
                  src="https://readdy.ai/api/search-image?query=Aerial%20drone%20view%20of%20forest%20land%20and%20timber%20assets%20showing%20tree%20canopy%20health%20monitoring%20access%20roads%20and%20boundary%20lines%20professional%20forestry%20documentation%20natural%20green%20tones%20clear%20detail%20institutional%20quality%20imagery&width=800&height=600&seq=portfolio-land-001&orientation=landscape"
                  alt="Land and timber monitoring"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  Land & Timber Assets
                </h3>
                <p className="text-gray-700 mb-4 leading-relaxed">
                  Ongoing monitoring of forest health, access, drainage, and boundary changes.
                </p>
                <div className="pt-4 border-t border-gray-200">
                  <p className="text-sm font-semibold text-gray-900 mb-2">Risk avoided:</p>
                  <p className="text-gray-700">
                    Unnoticed degradation that weakens long-term value.
                  </p>
                </div>
              </div>
            </div>

            {/* Area 2 */}
            <div className="bg-gray-50 rounded-lg overflow-hidden">
              <div className="h-64">
                <img
                  src="https://readdy.ai/api/search-image?query=Drone%20inspection%20of%20infrastructure%20assets%20including%20roads%20bridges%20towers%20and%20utility%20structures%20professional%20documentation%20perspective%20showing%20structural%20details%20neutral%20industrial%20tones%20clear%20visibility%20institutional%20inspection%20quality&width=800&height=600&seq=portfolio-infra-001&orientation=landscape"
                  alt="Infrastructure monitoring"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  Infrastructure & Utilities
                </h3>
                <p className="text-gray-700 mb-4 leading-relaxed">
                  Recurring inspection of roads, bridges, towers, substations, and vertical assets.
                </p>
                <div className="pt-4 border-t border-gray-200">
                  <p className="text-sm font-semibold text-gray-900 mb-2">Risk avoided:</p>
                  <p className="text-gray-700">
                    Deferred maintenance turning into emergency repairs.
                  </p>
                </div>
              </div>
            </div>

            {/* Area 3 */}
            <div className="bg-gray-50 rounded-lg overflow-hidden">
              <div className="h-64">
                <img
                  src="https://readdy.ai/api/search-image?query=Aerial%20drone%20view%20of%20land%20showing%20environmental%20monitoring%20erosion%20patterns%20wetland%20areas%20and%20regulated%20zones%20professional%20compliance%20documentation%20natural%20earth%20tones%20clear%20terrain%20detail%20institutional%20environmental%20assessment%20quality&width=800&height=600&seq=portfolio-env-001&orientation=landscape"
                  alt="Environmental monitoring"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  Environmental & Compliance Exposure
                </h3>
                <p className="text-gray-700 mb-4 leading-relaxed">
                  Tracking erosion, wet areas, and regulated zones.
                </p>
                <div className="pt-4 border-t border-gray-200">
                  <p className="text-sm font-semibold text-gray-900 mb-2">Risk avoided:</p>
                  <p className="text-gray-700">
                    Violations discovered after enforcement or damage.
                  </p>
                </div>
              </div>
            </div>

            {/* Area 4 */}
            <div className="bg-gray-50 rounded-lg overflow-hidden">
              <div className="h-64">
                <img
                  src="https://readdy.ai/api/search-image?query=Aerial%20drone%20documentation%20of%20construction%20site%20and%20capital%20project%20progress%20showing%20development%20phases%20equipment%20and%20site%20changes%20over%20time%20professional%20project%20monitoring%20perspective%20neutral%20construction%20tones%20clear%20detail%20institutional%20quality&width=800&height=600&seq=portfolio-construction-001&orientation=landscape"
                  alt="Construction monitoring"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  Construction & Capital Projects
                </h3>
                <p className="text-gray-700 mb-4 leading-relaxed">
                  Progress tracking and documentation across phases.
                </p>
                <div className="pt-4 border-t border-gray-200">
                  <p className="text-sm font-semibold text-gray-900 mb-2">Risk avoided:</p>
                  <p className="text-gray-700">
                    Disputes, delays, and undocumented changes.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How the Retainer Works */}
      <section id="how-it-works" className="py-24 bg-gray-50">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-gray-900 mb-16 text-center">
            How the Retainer Works
          </h2>
          
          <div className="grid md:grid-cols-4 gap-8 mb-12">
            <div className="text-center">
              <div className="w-16 h-16 bg-gray-900 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                1
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-3">
                Asset Portfolio Intake
              </h3>
              <p className="text-gray-700 leading-relaxed">
                Baseline survey and documentation of all assets
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-gray-900 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                2
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-3">
                Scheduled Inspections
              </h3>
              <p className="text-gray-700 leading-relaxed">
                Quarterly, semi-annual, or annual monitoring flights
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-gray-900 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                3
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-3">
                Change Detection
              </h3>
              <p className="text-gray-700 leading-relaxed">
                Automated flagging of issues and anomalies
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-gray-900 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                4
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-3">
                Centralized Reporting
              </h3>
              <p className="text-gray-700 leading-relaxed">
                Historical timeline and trend documentation
              </p>
            </div>
          </div>

          <p className="text-2xl text-center text-gray-900 font-medium mt-16">
            The value isn't the flight. It's the timeline.
          </p>
        </div>
      </section>

      {/* Who This Is For */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">
            Who This Is For
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className="flex items-start gap-4 p-6 bg-gray-50 rounded-lg">
              <div className="w-12 h-12 flex items-center justify-center flex-shrink-0">
                <i className="ri-funds-line text-3xl text-gray-900"></i>
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  Land and Timber Funds
                </h3>
                <p className="text-gray-700">
                  Track asset performance and health across portfolios
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4 p-6 bg-gray-50 rounded-lg">
              <div className="w-12 h-12 flex items-center justify-center flex-shrink-0">
                <i className="ri-building-line text-3xl text-gray-900"></i>
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  Infrastructure Owners
                </h3>
                <p className="text-gray-700">
                  Monitor critical assets and prevent costly failures
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4 p-6 bg-gray-50 rounded-lg">
              <div className="w-12 h-12 flex items-center justify-center flex-shrink-0">
                <i className="ri-government-line text-3xl text-gray-900"></i>
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  Utilities and Municipalities
                </h3>
                <p className="text-gray-700">
                  Maintain compliance and document infrastructure condition
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4 p-6 bg-gray-50 rounded-lg">
              <div className="w-12 h-12 flex items-center justify-center flex-shrink-0">
                <i className="ri-community-line text-3xl text-gray-900"></i>
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  Developers with Multiple Sites
                </h3>
                <p className="text-gray-700">
                  Track progress and changes across active projects
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4 p-6 bg-gray-50 rounded-lg md:col-span-2">
              <div className="w-12 h-12 flex items-center justify-center flex-shrink-0">
                <i className="ri-bank-line text-3xl text-gray-900"></i>
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  Family Offices and Institutional Owners
                </h3>
                <p className="text-gray-700">
                  Protect long-term value with continuous visibility
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="cta-section" className="relative py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Wide%20aerial%20view%20of%20well%20maintained%20land%20and%20infrastructure%20assets%20during%20golden%20hour%20showing%20professional%20asset%20management%20and%20oversight%20warm%20natural%20light%20institutional%20quality%20clear%20visibility%20professional%20documentation%20style&width=1920&height=1080&seq=portfolio-cta-001&orientation=landscape"
            alt="Asset oversight"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50"></div>
        </div>
        
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center text-white">
          <h2 className="text-4xl md:text-5xl font-bold mb-8">
            Turn Assets Into Something You Can Track
          </h2>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/instant-quote"
              className="px-8 py-4 bg-white text-gray-900 font-semibold rounded-lg hover:bg-gray-100 transition-colors whitespace-nowrap"
            >
              Request a Consultation
            </Link>
            <Link
              to="/pricing"
              className="px-8 py-4 bg-transparent text-white font-semibold rounded-lg border-2 border-white hover:bg-white/10 transition-colors whitespace-nowrap"
            >
              View Pricing
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}